import { useEffect, useState } from "react";
import { ScrollView, YStack } from "tamagui";

import type { PreClinicStep } from "@/api/generated/models";
import { useSendNoriMessage } from "@/api/mutations/conversation";
import { useUpdateUserProfile } from "@/api/mutations/userProfile";
import { useGetUserProfile } from "@/api/queries/userProfile";
import { ConstrainedView } from "@/components";
import PanelHeader from "@/conversation/AskUserQuestionsPanel/PanelHeader";
import { useCurrentConversationId } from "@/hooks/conversation";
import {
  PRE_CLINIC_STEP_PAGES,
  type PreClinicPage,
  selectIsPageAnswered,
  selectPreClinicPages,
  usePreClinicStepsStore,
} from "@/store/preClinicSteps";
import { useAskUserQuestionContainerStyles } from "../AskUserQuestionsPanel";
import ConfirmDetailsStep from "./ConfirmDetailsStep";
import CurrentStateStep from "./CurrentStateStep";
import DateOfBirthStep from "./DateOfBirthStep";
import DemographicsStep from "./DemographicsStep";
import InsuranceManualStep from "./InsuranceManualStep";
import InsurancePhotoStep from "./InsurancePhotoStep";
import InsuranceVerificationStep, {
  type InsuranceVerifyReason,
} from "./InsuranceVerificationStep";
import MedicareCardPhotoStep from "./MedicareCardPhotoStep";
import MedicareInsuranceManualStep from "./MedicareInsuranceManualStep";
import YesNoStep from "./YesNoStep";

// Stands in for the user, who answered in the panel rather than the composer.
const PRE_CLINIC_ANSWERED_MESSAGE = "I've added my details.";

const PAGE_TITLES: Record<PreClinicPage, string> = {
  has_insurance:
    "Do you have insurance that can offset the cost of your treatment?",
  photo: "Add your insurance",
  manual: "Add your insurance",
  medicare_photo: "Add your insurance",
  medicare_manual: "Add your insurance",
  has_affiliation: "Does your work or school pay for access to Curai Health?",
  confirm_details: "Confirm your details",
  date_of_birth: "What is your date of birth?",
  demographics: "A couple of details about you",
  state: "What state are you in?",
};

export default function PreClinicStepsPanel() {
  const {
    active: activeFlow,
    goToPage,
    setCollected,
    markStepResolved,
    getProgress,
    clearPreClinic,
  } = usePreClinicStepsStore();
  const conversationId = useCurrentConversationId();
  const active =
    activeFlow?.conversationId === conversationId ? activeFlow : null;
  const steps = active?.steps ?? [];
  const { data: profile } = useGetUserProfile();
  const { mutate: updateUserProfile } = useUpdateUserProfile();
  const { sendNoriMessage } = useSendNoriMessage(conversationId);
  const skipDateOfBirth = Boolean(profile?.dateOfBirth);

  const [phase, setPhase] = useState<"collecting" | "verifying" | "error">(
    "collecting",
  );
  const [verifyError, setVerifyError] = useState<InsuranceVerifyReason | null>(
    null,
  );
  const [verifyToken, setVerifyToken] = useState(0);

  const progress = getProgress(active?.clinicRequestId);
  const pages = selectPreClinicPages(steps, skipDateOfBirth)({ active });
  const firstUnresolved = steps.find(
    (step) => !progress.resolvedSteps.includes(step),
  );
  const isKnownPage = active ? pages.includes(active.page) : false;

  useEffect(() => {
    if (phase !== "collecting" || isKnownPage || !firstUnresolved) return;
    goToPage(PRE_CLINIC_STEP_PAGES[firstUnresolved][0]);
  }, [phase, isKnownPage, firstUnresolved, goToPage]);

  useEffect(() => {
    if (phase === "collecting" && active && !firstUnresolved) clearPreClinic();
  }, [phase, active, firstUnresolved, clearPreClinic]);

  const containerStyles = useAskUserQuestionContainerStyles();

  if (!active) return null;
  if (phase === "collecting" && !firstUnresolved) return null;

  const shouldVerifyInsurance =
    active.collected.hasInsurance === true &&
    active.collected.hasAffiliation !== true;

  const advance = (step: PreClinicStep) => {
    markStepResolved(active.clinicRequestId, step);
    const next = steps[steps.indexOf(step) + 1];
    if (next) {
      goToPage(PRE_CLINIC_STEP_PAGES[next][0]);
    } else if (shouldVerifyInsurance) {
      setPhase("verifying");
    } else {
      clearPreClinic();
      // Sent as an ordinary message so the turn the generalist is waiting on is a real user
      // turn, which is what the input guardrails judge. Only from here, so the effect that
      // clears an already-resolved session cannot send a second one.
      if (active.resumesTurn) {
        sendNoriMessage(PRE_CLINIC_ANSWERED_MESSAGE);
      }
    }
  };

  const reverifyInsurance = () => {
    setVerifyError(null);
    setVerifyToken((token) => token + 1);
    setPhase("verifying");
  };

  const proceedWithoutInsurance = () => {
    updateUserProfile(
      {
        data: {
          carrierName: "",
          bridgePayerId: "",
          planName: "",
          memberId: "",
          groupNumber: "",
          rxBin: "",
          rxPcn: "",
          rxGroup: "",
        },
      },
      {
        onSuccess: () => {
          setCollected({ hasInsurance: false, insurance: null });
          clearPreClinic();
        },
      },
    );
  };

  const fallbackPage = firstUnresolved
    ? PRE_CLINIC_STEP_PAGES[firstUnresolved][0]
    : active.page;
  const activePage = isKnownPage ? active.page : fallbackPage;
  const pageIndex = pages.indexOf(activePage);
  const isVerifying = phase === "verifying";
  const nextDisabled =
    !selectIsPageAnswered(activePage)({ active }) || isVerifying;
  const title = isVerifying
    ? "Verifying your insurance"
    : activePage === "manual" && active.collected.insurance
      ? "Confirm your insurance information"
      : PAGE_TITLES[activePage];

  return (
    <ConstrainedView>
      <YStack {...containerStyles}>
        <PanelHeader
          question={title}
          index={pageIndex}
          total={pages.length}
          onPrev={() => goToPage(pages[pageIndex - 1])}
          onNext={() => goToPage(pages[pageIndex + 1])}
          nextDisabled={nextDisabled}
        />
        <ScrollView flexGrow={0} flexShrink={1} flexBasis="auto" minHeight={0}>
          {isVerifying ? (
            <InsuranceVerificationStep
              key={verifyToken}
              hasAffiliation={active.collected.hasAffiliation === true}
              onVerified={clearPreClinic}
              onFailed={(reason) => {
                setVerifyError(reason);
                setPhase("error");
                goToPage("manual");
              }}
            />
          ) : (
            <>
              {activePage === "has_insurance" && (
                <YesNoStep
                  value={active.collected.hasInsurance}
                  onYes={() => {
                    setCollected({ hasInsurance: true });
                    goToPage("photo");
                  }}
                  onNo={() => {
                    setCollected({ hasInsurance: false });
                    advance("insurance");
                  }}
                />
              )}
              {activePage === "photo" && (
                <InsurancePhotoStep onAddManually={() => goToPage("manual")} />
              )}
              {activePage === "manual" && (
                <InsuranceManualStep
                  insurance={active.collected.insurance}
                  onTakePhoto={() => goToPage("photo")}
                  onSubmitted={
                    phase === "error"
                      ? reverifyInsurance
                      : () => advance("insurance")
                  }
                  errorReason={phase === "error" ? verifyError : null}
                  onProceedWithoutInsurance={proceedWithoutInsurance}
                />
              )}
              {activePage === "medicare_photo" && (
                <MedicareCardPhotoStep
                  onAddManually={() => goToPage("medicare_manual")}
                />
              )}
              {activePage === "medicare_manual" && (
                <MedicareInsuranceManualStep
                  medicareCard={active.collected.medicareCard}
                  onTakePhoto={() => goToPage("medicare_photo")}
                  onSubmitted={() => advance("medicare_insurance")}
                />
              )}
              {activePage === "has_affiliation" && (
                <YesNoStep
                  value={active.collected.hasAffiliation}
                  onYes={() => {
                    setCollected({ hasAffiliation: true });
                    goToPage("confirm_details");
                  }}
                  onNo={() => {
                    setCollected({ hasAffiliation: false });
                    if (skipDateOfBirth) {
                      advance("affiliation");
                    } else {
                      goToPage("date_of_birth");
                    }
                  }}
                />
              )}
              {activePage === "confirm_details" && (
                <ConfirmDetailsStep
                  onSubmitted={() => advance("affiliation")}
                />
              )}
              {activePage === "date_of_birth" && (
                <DateOfBirthStep onSubmitted={() => advance("affiliation")} />
              )}
              {activePage === "demographics" && (
                <DemographicsStep
                  onSubmitted={() => advance("demographics")}
                  isLast={pageIndex === pages.length - 1}
                />
              )}
              {activePage === "state" && (
                <CurrentStateStep
                  onSubmitted={() => advance("current_state")}
                />
              )}
            </>
          )}
        </ScrollView>
      </YStack>
    </ConstrainedView>
  );
}
